import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { STATUS_COLOR_CLASS, type StatusColorTone } from '@/styles/statusColors';

export type StateMessageSize = 'default' | 'compact';

interface StateMessageSizeClasses {
  stack: string;
  tile: string;
  icon: string;
  title: string;
}

const SIZE_CLASSES: Record<StateMessageSize, StateMessageSizeClasses> = {
  default: {
    stack: 'max-w-sm gap-6',
    tile: 'size-14 ring-8',
    icon: 'size-6',
    title: 'text-base',
  },
  compact: {
    stack: 'max-w-xs gap-3',
    tile: 'size-10 ring-4',
    icon: 'size-5',
    title: 'text-sm',
  },
};

const TONE_RING_CLASS: Record<StatusColorTone, string> = {
  success: 'ring-success/40',
  warning: 'ring-warning/40',
  info: 'ring-info/40',
  danger: 'ring-danger/40',
  neutral: 'ring-muted',
};

export interface StateMessageProps {
  icon: LucideIcon;
  tone: StatusColorTone;
  title: string;
  description?: string | undefined;
  actions?: ReactNode;
  note?: ReactNode;
  size?: StateMessageSize | undefined;
}

export function StateMessage({
  icon: Icon,
  tone,
  title,
  description,
  actions,
  note,
  size = 'default',
}: StateMessageProps) {
  const sizeClasses = SIZE_CLASSES[size];

  return (
    <div className={cn('flex w-full flex-col items-center text-center', sizeClasses.stack)}>
      <span
        className={cn(
          'flex items-center justify-center rounded-2xl',
          sizeClasses.tile,
          STATUS_COLOR_CLASS[tone],
          TONE_RING_CLASS[tone],
        )}
      >
        <Icon className={sizeClasses.icon} aria-hidden />
      </span>

      <div className="flex flex-col gap-1.5">
        <h3 className={cn('font-semibold tracking-tight', sizeClasses.title)}>{title}</h3>
        {description ? (
          <p className="text-sm leading-relaxed text-balance text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>

      {actions ? (
        <div className="flex flex-wrap items-center justify-center gap-2">{actions}</div>
      ) : null}
      {note ? <p className="text-xs text-muted-foreground/70">{note}</p> : null}
    </div>
  );
}
